import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function EmployeesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");

  const canFilterDept = ["admin", "hr_manager"].includes(user.role);
  const showSalary = ["admin", "hr_manager"].includes(user.role);

  useEffect(() => {
    if (canFilterDept) {
      api.get("/employees/departments").then((r) => setDepartments(r.data.departments)).catch(() => {});
    }
  }, [canFilterDept]);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (dept !== "all") params.department = dept;
    const t = setTimeout(() => {
      api.get("/employees", { params }).then((r) => setEmployees(r.data.employees)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, dept]);

  return (
    <div className="space-y-5 fade-in" data-testid="employees-page">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="employees-search-input"
            placeholder={t("employees.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none h-9 ps-9 text-[13px]"
          />
        </div>
        {canFilterDept && (
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="rounded-none h-9 w-56 text-[13px]" data-testid="employees-dept-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none text-[13px]">{t("employees.allDepartments")}</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d} value={d} className="rounded-none text-[13px]">{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {employees && (
          <span className="text-[12px] font-mono text-muted-foreground ms-auto" data-testid="employees-count">
            {t("employees.count", { count: employees.length })}
          </span>
        )}
      </div>

      {!employees ? (
        <Skeleton className="h-96 w-full rounded-none" />
      ) : (
        <div className="bg-white border border-border">
          <Table data-testid="employees-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-[11px] uppercase tracking-[0.1em] ps-5">{t("employees.colEmployee")}</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">{t("employees.colDepartment")}</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">{t("employees.colLevel")}</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">{t("employees.colTopSkills")}</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em] text-end">{t("employees.colKpi")}</TableHead>
                {showSalary && <TableHead className="text-[11px] uppercase tracking-[0.1em] text-end pe-5">{t("employees.colSalary")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow
                  key={e.id}
                  data-testid={`employee-row-${e.id}`}
                  className="border-border hover:bg-slate-50/70 cursor-pointer"
                  onClick={() => navigate(`/employees/${e.id}`)}
                >
                  <TableCell className="pl-5">
                    <p className="text-[13px] font-medium text-slate-800">{e.name}</p>
                    <p className="text-[11px] text-muted-foreground">{e.title}</p>
                  </TableCell>
                  <TableCell className="text-[12px] text-slate-600">{e.department}</TableCell>
                  <TableCell className="text-[12px] font-mono text-slate-500">L{e.level}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(e.skills || []).slice(0, 3).map((s) => (
                        <span key={s.name} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 border border-border">
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-[12px] text-slate-700">
                    {e.kpi_scores?.overall ?? "—"}
                  </TableCell>
                  {showSalary && (
                    <TableCell className="text-right font-mono text-[12px] text-slate-700 pr-5">
                      {e.salary_qar?.toLocaleString() ?? "—"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
